<template>
  <div class="product-navigation">
    <div
      class="product-navigation__container"
      :class="{'product-navigation__container--sticky': stickyState}"
      ref="productNavigationElement"
    >
      <transition name="product-navigation--animation-row">
        <div
          class="product-navigation__row"
          v-if="stickyState"
        >
          <sale-box-bar
            class="product-navigation__sale-box"
            :one-page-checkout="onePageCheckout"
          />
        </div>
      </transition>

      <nav
        v-if="showNavi"
        class="product-navigation__row product-navigation__row--nav"
      >
        <ul class="product-navigation__wrapper">
          <template
            v-for="(section, index) in sections"
          >
            <li
              v-if="getSectionText(section).length > 0"
              :key="index"
              class="product-navigation__item"
            >
              <a
                :href="getSectionUrl(section)"
                @click.prevent="scrollTo(section)"
                class="product-navigation__link"
                :class="{ 'product-navigation__link--active': section === currentSection }"
              >
                {{ getSectionText(section) }}
              </a>
            </li>
          </template>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script lang="ts">
  import {Vue, Options, Prop} from 'vue-property-decorator';
  import {throttle} from '../../../bps/utilities/js/throttle/throttle';
  import pigeon from '../../../bps/utilities/js/pigeon/pigeon';
  import * as scrollToElement from 'scroll-to-element';
  import {breakpoints} from '../../../bps/variables/variables.js';
  import SaleBoxBar from '../saleBoxBar/saleBoxBar.vue';
  import productStore, {ProductStore} from '../../../bps/stores/product/ProductStore';

  @Options({
    name: 'product-navigation',
    components: {SaleBoxBar}
  })
  export default class ProductNavigation extends Vue {
    @Prop({required: true})
    readonly sectionSelector: string;

    @Prop({required: true})
    readonly headlineSelector: string;

    @Prop()
    readonly offsetBasis: number;

    @Prop()
    readonly offsetCurrentTop: number;

    @Prop({required: false, default: 0})
    readonly offsetStickyHeight: number;

    @Prop({default: true})
    readonly showNavi: boolean;

    @Prop({default: false})
    readonly onePageCheckout: boolean;

    @Prop({default: 100})
    readonly elementHeight: number; // Height of component

    sections: HTMLElement[] = [];
    currentSection: HTMLElement|null = null;
    goToTopCta: HTMLElement|null = document.querySelector('.go-top');
    stickyState: boolean = false;
    sectionParamName: string = 'section';
    productStore: ProductStore = productStore;

    mounted() {
      window.addEventListener('scroll', throttle(this.onScroll, 150));

      this.updateSections();
      pigeon.subscribe('productPage::variationChange', this.updateSections);
      pigeon.subscribe('productAwards:updateCount', this.updateSections);

      // If section parameter is set in url, scroll to section
      const sectionParamValue = this.getHashParam(this.sectionParamName);
      if (sectionParamValue) {
        window.setTimeout(() => this.scrollToSectionByText(decodeURI(sectionParamValue)), 1000);
      }
    }

    updateSections() {
      this.sections = Array.prototype.slice.call(document.querySelectorAll(this.sectionSelector));
    }

    get offset(): number {
      return -this.offsetBasis - this.elementHeight;
    }

    get showBottomSalesBox(): boolean {
      return !this.productStore.productDetails.product.purchaseOnRequestArticle
        && this.productStore.productDetails.product.availability.available;
    }

    getHashParam(param: string) {
      let value = '';
      const hash = window.location.hash;
      const paramStr: string = `#${param}=`;
      if (hash.indexOf(paramStr) != -1) {
        value = hash.split(paramStr)[1].split('#')[0];
      }

      return value;
    }

    getSectionText(sectionElement: HTMLElement): string {
      let text = '';
      const headlineElement: HTMLElement = sectionElement.querySelector(this.headlineSelector);
      if (headlineElement && headlineElement.getAttribute('title')) {
        text = headlineElement.getAttribute('title').trim();
      } else if (headlineElement) {
        text = headlineElement.innerText.trim();
      }
      return text;
    }

    scrollToSectionByText(text: string) {
      let ndx: number = this.sections.length;
      while (ndx--) {
        const section: HTMLElement = this.sections[ndx];
        if (this.getSectionText(section) === text) {
          this.scrollTo(section);
          break;
        }
      }
    }

    scrollTo(item: HTMLElement) {
      this.stickyState = !!this.stickyState; // clear computed cache for offset
      let offsetHeight = this.offset;
      if (!this.stickyState) {
        const productNavigationElement : any = this.$refs.productNavigationElement;
        offsetHeight -= productNavigationElement.getBoundingClientRect().height;
        offsetHeight += this.offsetStickyHeight;
      }

      scrollToElement(item, {
        offset: offsetHeight,
        duration: 400
      });
    }

    onScroll() {
      if (window.innerWidth >= breakpoints.tablet) {
        this.updateStickyState();
        this.updateCurrentSection();
      } else if (this.showBottomSalesBox) {
        this.updateStickyState();
        if (this.goToTopCta && !this.goToTopCta.classList.contains('go-top--with-bottom-space')) {
          this.goToTopCta.classList.add('go-top--with-bottom-space');
        }
      }
    }

    updateStickyState() {
      this.stickyState = Math.floor(this.$el.getBoundingClientRect().top - this.offsetBasis) <= 0;
      this.toggleHiddenClassWhenSticky()
    }

    toggleHiddenClassWhenSticky() {
      const headerEl: HTMLElement = document.querySelector('.header__wrapper');
      if (headerEl) {
        if (this.stickyState) {
          if (headerEl) {
            headerEl.classList.add('header--hide')
          }
        } else {
          headerEl.classList.remove('header--hide')
        }
      }
    }

    updateCurrentSection() {
      if (!this.showNavi) {
        return;
      }

      let cur: HTMLElement|null = null;
      let ndx = this.sections.length;
      while (ndx--) {
        const item: HTMLElement = this.sections[ndx];
        const itemBounds: ClientRect = item.getBoundingClientRect();
        const topBound: number = Math.round(itemBounds.top + this.offset + this.offsetCurrentTop);
        const bottomBound: number = itemBounds.bottom + this.offset + this.offsetCurrentTop;
        if (topBound <= 0 && bottomBound >= 0) {
          cur = item;
          break;
        }
      }

      this.currentSection = cur;
    }

    getSectionUrl(section: HTMLElement) {
      const sectionParam = encodeURIComponent(this.getSectionText(section));
      return `#${this.sectionParamName}=${sectionParam}`
    }
  }
</script>
